import React from "react";
import { encodeListBuilderImport, savedSearchStateToListBuilderImport } from "./icpListBuilder";

// A search the agent drafted from its description ("VP Sales in US manufacturing companies"): what it
// filters on, who it finds right now, and a way to review it in List Builder. On create it's saved as a
// List Builder saved search, so it runs — and can be refined later — like any saved search.

const FIELD_LABELS = {
  titles: "titles", exclude_titles: "excluded titles", levels: "seniority", departments: "job functions",
  locations: "locations", company_locations: "HQ locations", industries: "industries",
  exclude_industries: "excluded industries", employee_count: "employee sizes", revenue: "revenue",
  funding_rounds: "funding", technologies: "technologies", company_domains: "domains"
};

// Industry names are stored lowercase (List Builder's vocabulary); show them capitalised.
const CAPITALISE = ["Industry", "Industry excludes", "Technologies"];
const capitalise = s => String(s).replace(/(^|[\s/(-])([a-z])/g, (m, pre, c) => pre + c.toUpperCase());

const displayValues = item => (item.values || []).map(v => (CAPITALISE.includes(item.label) ? capitalise(v) : v));

const shortList = (values, max) => {
  const shown = values.slice(0, max).join(", ");
  return values.length > max ? `${shown} +${values.length - max}` : shown;
};

const totalOf = described => {
  const p = described && described.preview;
  return p && typeof p.total === "number" ? p.total : null;
};

/** Collapsed-step summary: count first (so it survives truncation), then the key filters. */
export const describedSearchText = (described, companies) => {
  if (!described || !described.search) return "";
  const total = totalOf(described);
  const parts = (described.summary || []).map(item => shortList(displayValues(item), 1)).filter(Boolean);
  const count = total === null ? [] : [`${total.toLocaleString()} ${companies ? "companies" : "people"}`];
  return [...count, ...parts.slice(0, 3)].join(" · ");
};

const initials = name => String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");

const DescribedSearch = ({ described, companies, agentName, onUseSavedSearch }) => {
  const { summary = [], preview, unresolved = [], refreshing, edited } = described || {};
  const rows = (preview && preview.rows) || [];
  const total = totalOf(described);
  const noun = companies ? "companies" : "people";
  const searchName = `${(agentName || "").trim() || "Agent"} — search`;
  const openInListBuilder = () => {
    const payload = {
      ...savedSearchStateToListBuilderImport(described.search, companies),
      // Lets List Builder send refined filters back to this draft.
      agentSearch: { token: described.token, name: (agentName || "").trim() || "your agent" }
    };
    window.open(`/list-builder?sgLb=${encodeListBuilderImport(payload)}`, "_blank", "noopener");
  };

  return (
    <div className="af-dsearch">
      <div className="af-dsearch__head">
        <span className="af-dsearch__eyebrow">
          Who it finds{edited && <span className="af-dsearch__edited"> · updated in List Builder</span>}
        </span>
        <span className="af-dsearch__actions">
          <button type="button" className="af-dsearch__swap" onClick={onUseSavedSearch}>Use a saved search instead</button>
          <button type="button" className="af-dsearch__open" onClick={openInListBuilder}>
            Review in List Builder <i className="material-icons-outlined">open_in_new</i>
          </button>
        </span>
      </div>

      <dl className="af-dsearch__filters">
        {summary.map(item => {
          const values = displayValues(item);
          return (
            <React.Fragment key={item.label}>
              <dt>{item.label}</dt>
              <dd title={values.join(", ")}>{shortList(values, 3)}</dd>
            </React.Fragment>
          );
        })}
      </dl>

      {unresolved.length > 0 && (
        <div className="af-dsearch__warn">
          <i className="material-icons-outlined">error_outline</i>
          <span>
            Left out — List Builder has no match for{" "}
            {unresolved.map(u => `“${u.value}” (${FIELD_LABELS[u.field] || u.field})`).join(", ")}.
          </span>
        </div>
      )}

      {refreshing ? (
        <div className="af-dsearch__preview af-dsearch__preview--muted">Checking the updated search…</div>
      ) : preview && preview.error ? (
        <div className="af-dsearch__preview af-dsearch__preview--muted">
          Couldn’t preview this search right now — it will still run.
        </div>
      ) : total === 0 ? (
        <div className="af-dsearch__warn">
          <i className="material-icons-outlined">search_off</i>
          <span>No {noun} match this search right now. Review it in List Builder to loosen the filters.</span>
        </div>
      ) : total !== null ? (
        <div className="af-dsearch__preview">
          <div className="af-dsearch__count">
            <span><b>{total.toLocaleString()}</b> {noun} match right now</span>
            {rows.length > 0 && <span className="af-dsearch__sample">Sample</span>}
          </div>
          {rows.map((r, idx) => (
            <div className="af-dsearch__person" key={idx}>
              <span className="af-dsearch__avatar">{initials(r.name || r.domain)}</span>
              <span className="af-dsearch__who">
                <span className="af-dsearch__name">{r.name || r.domain}</span>
                <span className="af-dsearch__sub">{companies ? r.domain : r.title}</span>
              </span>
              <span className="af-dsearch__where">
                <span className="af-dsearch__org">{companies ? capitalise(r.industry || "") : r.company}</span>
                <span className="af-dsearch__sub">{r.location}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <span className="af-dsearch__note">
        <i className="material-icons-outlined">bookmark_border</i>
        Saved to List Builder as “{searchName}” when you create the agent
      </span>
    </div>
  );
};

export default DescribedSearch;
